import type { ReactNode } from "react";
import { useWorkspace } from "@/app/WorkspaceContext";
import { ProvenanceTag } from "@/ui";
import type { Source } from "@/mock";

/** The Signals panels, in order. Route is /signals/:panel.
 *  `search` = placeholder for the panel's row filter (omitted = no search box). */
export const PANELS = [
  { value: "overview", label: "Overview", search: "Search sources & pages" },
  { value: "realtime", label: "Realtime" },
  { value: "behavior", label: "Behavior" },
  { value: "acquisition", label: "Acquisition", search: "Search channels & sources" },
  { value: "audience", label: "Audience", search: "Search countries & devices" },
  { value: "funnels", label: "Funnels" },
  { value: "experiments", label: "A/B tests", search: "Search A/B tests" },
  { value: "events", label: "Events", search: "Search events" },
  { value: "replays", label: "Replays", search: "Search sessions, devices, locations" },
  { value: "heatmaps", label: "Heatmaps" },
  { value: "dashboard", label: "My dashboard" },
] as const;

export type PanelKey = (typeof PANELS)[number]["value"];

export function isPanel(v: string | undefined): v is PanelKey {
  return !!v && PANELS.some((p) => p.value === v);
}

/** Human-readable label for the active range, for quiet panel captions. */
export function rangeLabel(range: string): string {
  switch (range) {
    case "24h":
      return "last 24 hours";
    case "7d":
      return "last 7 days";
    case "30d":
      return "last 30 days";
    case "90d":
      return "last 90 days";
    default:
      return range;
  }
}

/** A small per-panel header: title + quiet description + optional provenance + action. */
export function PanelHeader({
  title,
  desc,
  source,
  action,
}: {
  title: ReactNode;
  desc?: ReactNode;
  source?: Source;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-[17px] font-semibold tracking-tight text-ink-strong">{title}</h2>
        {desc && <p className="mt-0.5 text-[13px] text-muted">{desc}</p>}
      </div>
      <div className="flex items-center gap-3">
        {source && <ProvenanceTag source={source} />}
        {action}
      </div>
    </div>
  );
}

/** Date-range chip control wired to the shared workspace range. */
export function RangeChips() {
  const { range, setRange } = useWorkspace();
  const ranges: { value: "24h" | "7d" | "30d" | "90d"; label: string }[] = [
    { value: "24h", label: "24h" },
    { value: "7d", label: "7d" },
    { value: "30d", label: "30d" },
    { value: "90d", label: "90d" },
  ];
  return (
    <div className="inline-flex rounded-full border border-hairline bg-paper p-0.5 shadow-sm dark:shadow-none">
      {ranges.map((r) => {
        const active = r.value === range;
        return (
          <button
            key={r.value}
            onClick={() => setRange(r.value)}
            className={
              "rounded-full px-3 py-1.5 font-mono text-[11px] uppercase tracking-wide transition-colors " +
              (active
                ? "bg-paper-2 text-ink-strong dark:bg-white/10"
                : "text-muted hover:text-ink")
            }
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );
}
